import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import apiService from '../../services/apiService';
import { toast } from 'react-toastify';

const Hotel = () => {
  const navigate = useNavigate();
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dateRange, setDateRange] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const recordsPerPage = 10;
  const user = JSON.parse(localStorage.getItem('user'));
  const mediaUrl = process.env.REACT_APP_MEDIA_URL;

  useEffect(() => {
    fetchHotels();
  }, [currentPage, search, dateRange]);

  const fetchHotels = async () => {
    try {
      setLoading(true);
      const response = await apiService.post('hotels', {
        start: (currentPage - 1) * recordsPerPage,
        length: recordsPerPage,
        search: search,
        date: dateRange
      });
      setHotels(response.data || []);
      setTotalRecords(response.recordsTotal || 0);
    } catch (error) {
      toast.error('Failed to fetch hotels');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusToggle = async (id) => {
    try {
      await apiService.get(`hotelStatus/${id}`);
      fetchHotels();
    } catch (error) {
      toast.error('Something went wrong, try again!!');
    }
  };

  const handleDelete = async () => {
    setSubmitting(true);
    try {
      await apiService.get(`deleteHotel/${selectedId}`);
      toast.success('Hotel deleted successfully');
      setShowDeleteModal(false);
      fetchHotels();
    } catch (error) {
      toast.error('Something went wrong, try again!!');
    } finally {
      setSubmitting(false);
    }
  };

  const totalPages = Math.ceil(totalRecords / recordsPerPage);
  const startRecord = totalRecords === 0 ? 0 : (currentPage - 1) * recordsPerPage + 1;
  const endRecord = Math.min(currentPage * recordsPerPage, totalRecords);

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => setCurrentPage(i)}
          className={`px-3 py-1 rounded ${
            currentPage === i
              ? 'text-white'
              : 'bg-white text-gray-700 hover:bg-gray-100'
          }`}
          style={currentPage === i ? { backgroundColor: '#009ef7' } : {}}
        >
          {i}
        </button>
      );
    }
    return pages;
  };

  return (
    <div className="flex-1 p-0">
      <div className="bg-white rounded-lg shadow">
        <div className="flex items-center justify-between p-3 border-b">
          <h3 className="text-lg font-semibold text-gray-900">Hotels</h3>
          <button
            onClick={() => navigate('/hotel/create')}
            className="flex items-center px-2 sm:px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
            style={{ backgroundColor: '#009ef7' }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#0084d4'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#009ef7'}
          >
            <i className="bi bi-plus text-sm mr-1"></i>
            Add New
          </button>
        </div>

        <div className="p-6">
          <div className="flex gap-4 mb-6">
            <div className="relative flex-1">
              <i className="bi bi-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Type Name, Phone or Email"
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
              />
            </div>
            <input
              type="text"
              value={dateRange}
              onChange={(e) => {
                setDateRange(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Pick date range"
              className="w-80 px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
            />
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#009ef7]"></div>
            </div>
          ) : hotels.length === 0 ? (
            <div className="text-center py-12 text-gray-500">No hotels found</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-xs sm:text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                      <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase min-w-[200px]">Image</th>
                      <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase min-w-[200px]">Business</th>
                      <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                      <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                      <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Live</th>
                      <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Added On</th>
                      <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {hotels.map((hotel, index) => (
                      <tr key={hotel._id}>
                        <td className="px-2 sm:px-4 py-4 text-sm text-gray-900">
                          {startRecord + index}
                        </td>
                        <td className="px-2 sm:px-4 py-4">
                          <img
                            src={hotel.image ? `${mediaUrl}${hotel.image}` : '/assets/media/avatars/300-1.jpg'}
                            alt={hotel.name}
                            className="h-20 w-40 object-cover rounded"
                          />
                        </td>
                        <td className="px-2 sm:px-4 py-4">
                          <div className="flex flex-col gap-1">
                            {hotel.vendorId ? (
                              <>
                                <Link to={`/vendorWallet/${hotel.vendorId._id}`} className="text-sm text-gray-900 hover:text-[#009ef7] font-medium no-underline">
                                  {hotel.vendorId.business_name || '-'}
                                </Link>
                                {hotel.vendorId.slug && (
                                  <a
                                    href={`https://www.travellinglark.com/agent/${hotel.vendorId.slug}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-block px-2 py-1 text-[11px] font-semibold bg-cyan-100 text-cyan-800 rounded-md hover:bg-cyan-200 w-fit no-underline"
                                  >
                                    <i className="bi bi-building mr-1"></i>View Profile
                                  </a>
                                )}
                              </>
                            ) : (
                              <span className="text-gray-400">--</span>
                            )}
                          </div>
                        </td>
                        <td className="px-2 sm:px-4 py-4 text-sm text-gray-900">{hotel.name}</td>
                        <td className="px-2 sm:px-4 py-4 text-sm text-gray-900">{hotel.price}</td>
                        <td className="px-2 sm:px-4 py-4">
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={hotel.status === 1}
                              onChange={() => handleStatusToggle(hotel._id)}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#009ef7]"></div>
                          </label>
                        </td>
                        <td className="px-2 sm:px-4 py-4 text-sm text-gray-900">
                          {new Date(hotel.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-2 sm:px-4 py-4">
                          <div className="flex gap-2">
                            <a
                              href={`https://travellinglark.com/hotel/${hotel.slug}/${hotel.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 rounded hover:bg-[#009ef7]/10"
                              style={{ color: '#009ef7' }}
                              title="View"
                            >
                              <i className="bi bi-eye text-lg"></i>
                            </a>
                            <button
                              onClick={() => navigate(`/hotel/edit/${hotel._id}`)}
                              className="p-2 rounded hover:bg-green-50"
                              style={{ color: '#50cd89' }}
                              title="Edit"
                            >
                              <i className="bi bi-pencil text-sm"></i>
                            </button>
                            <button
                              onClick={() => {
                                setSelectedId(hotel._id);
                                setShowDeleteModal(true);
                              }}
                              className="p-2 rounded hover:bg-red-50"
                              style={{ color: '#f1416c' }}
                              title="Delete"
                            >
                              <i className="bi bi-trash text-sm"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between mt-6">
                <div className="text-sm text-gray-700">
                  Showing {startRecord} to {endRecord} of {totalRecords} results
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 bg-white text-gray-700 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  {renderPagination()}
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 bg-white text-gray-700 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">Confirmation</h3>
              <button onClick={() => setShowDeleteModal(false)} className="text-gray-400 hover:text-gray-600">
                <i className="bi bi-x text-2xl"></i>
              </button>
            </div>
            <div className="p-6">
              <p className="text-gray-700">Are you sure you want to delete this Hotel?</p>
            </div>
            <div className="flex flex-col sm:flex-row justify-end gap-3 p-4 border-t">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-2 sm:px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={submitting}
                className="px-2 sm:px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
              >
                {submitting ? 'Please wait...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Hotel;
